import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/", label: "홈" },
  { to: "/roll-press", label: "롤프레스" },
  { to: "/servo-hydraulic", label: "서보유압" },
  { to: "/products", label: "제품" },
  { to: "/engineering", label: "엔지니어링" },
  { to: "/company", label: "회사소개" },
  { to: "/contact", label: "문의하기" },
];

interface NavbarProps {
  variant?: "transparent" | "solid";
}

export default function Navbar({ variant = "solid" }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isTransparent = variant === "transparent" && !scrolled;

  return (
    <header
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b",
        isTransparent
          ? "bg-transparent border-background-50/10"
          : "bg-background-50/95 backdrop-blur border-background-200",
      ].join(" ")}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 cursor-pointer whitespace-nowrap"
          >
            <img
              src="https://static.readdy.ai/image/8ccd4206efe4c67b5e0d83664e55b718/1ca14cfadd9a8668780a4553609321f7.png"
              alt="MC TECH"
              className="w-9 h-9 object-contain rounded"
            />
            <span className="flex flex-col leading-tight">
              <span
                className={[
                  "font-heading font-extrabold tracking-tight text-lg",
                  isTransparent ? "text-background-50" : "text-primary-800",
                ].join(" ")}
              >
                MC TECH
              </span>
              <span
                className={[
                  "text-[10px] font-medium tracking-[0.15em] uppercase",
                  isTransparent ? "text-background-100/80" : "text-secondary-600",
                ].join(" ")}
              >
                Roll Press Engineering
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "px-3 xl:px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer text-[13px] font-semibold tracking-wide",
                    isActive
                      ? isTransparent
                        ? "text-background-50 bg-background-50/15"
                        : "text-primary-700 bg-primary-50"
                      : isTransparent
                      ? "text-background-100/90 hover:text-background-50 hover:bg-background-50/10"
                      : "text-foreground-700 hover:text-primary-700 hover:bg-background-100",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:031-736-3275"
              className={[
                "flex items-center gap-2 text-[13px] font-semibold whitespace-nowrap cursor-pointer",
                isTransparent ? "text-background-100" : "text-secondary-700",
              ].join(" ")}
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
              <span>031-736-3275</span>
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-[13px] font-semibold whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
              견적 문의
            </Link>
          </div>

          {/* Mobile burger */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className={[
              "lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer",
              isTransparent
                ? "text-background-50 hover:bg-background-50/15"
                : "text-foreground-800 hover:bg-background-100",
            ].join(" ")}
          >
            <i
              className={[
                "text-xl flex items-center justify-center w-6 h-6",
                open ? "ri-close-line" : "ri-menu-line",
              ].join(" ")}
            />
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-background-200 py-3 bg-background-50">
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    [
                      "px-3 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer",
                      isActive
                        ? "text-primary-700 bg-primary-50"
                        : "text-foreground-700 hover:bg-background-100",
                    ].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center" />
                견적 문의
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}